// src/redux/slices/authSlice.tsx
import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";

// Type definitions for auth state
interface UserDetails {
  id: string;
  email?: string;
  [key: string]: unknown;
}

interface Permission {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface AuthState {
  userDetails: UserDetails | null;
  permissions: Permission[];
  // Distinguishes "permissions not fetched yet" (false) from "fetched, and
  // the user genuinely has none" (true, permissions === []). ProtectedRoute
  // relies on this so an empty array during the load window can't be mistaken
  // for "no restrictions" — see 2026-06-10 audit C7.
  permissionsLoaded: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isMember: boolean;
}

const initialState: AuthState = {
  userDetails: null,
  permissions: [],
  permissionsLoaded: false,
  isAdmin: false,
  isStaff: false,
  isMember: false,
};

// Normalize role for consistent comparison (handles spaces, underscores, case)
const normalizeRole = (role: string): string => role.toLowerCase().replace(/\s+/g, "_");

// Admin-equivalent roles (normalized form)
const ADMIN_ROLES = new Set(["admin", "super_admin", "tenant_owner", "platform_admin"]);

// Check if a role has admin privileges
const isAdminRole = (role: string | null | undefined): boolean => {
  if (!role) return false;
  return ADMIN_ROLES.has(normalizeRole(role));
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.userDetails = action.payload;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload || [];
      // Mark loaded on any setPermissions dispatch — including an empty array,
      // which is the legitimate "this user has no explicit permissions" state.
      state.permissionsLoaded = true;
    },
    /**
     * Clears the admin bit. Takes a BOOLEAN, never a role name.
     *
     * f4707e27/cb8a2421 removed every caller that set this from the role
     * STRING, because `users.role` is the drifted column the API does not
     * authorize on — on production the string and the server disagreed for 5
     * of 14 staff accounts, in both directions. `setAuthzFromCapabilities` is
     * now the only thing that grants admin, from the server's role_id answer.
     *
     * But the reducer still DERIVED from a role string, so it only happened to
     * be safe: `isAdminRole(false)` is false by falsiness, while
     * `isAdminRole("admin")` is still true. One future
     * `dispatch(setIsAdmin(user.role))` would have silently reinstated the bug
     * those commits removed, and nothing would have caught it.
     *
     * Strict `=== true` closes that door: a role name can no longer grant
     * admin here no matter what a caller passes. Both live call sites pass
     * false on logout and are unaffected.
     */
    setIsAdmin: (state, action) => {
      state.isAdmin = action.payload === true;
    },
    setIsStaff: (state, action) => {
      const role = action.payload;
      // Treat any role that isn't admin, member, customer, or kiosk as staff
      // This allows custom staff roles like "manager", "junior_staff", "senior_coach", etc.
      const nonStaffRoles = ["member", "customer", "kiosk"];
      state.isStaff = Boolean(role) &&
        !isAdminRole(role) &&
        !nonStaffRoles.includes(role);
    },
    setIsMember: (state, action) => {
      // Customers are member-portal accounts too (see utils/roles
      // isMemberRole) — enrolling auto-promotes them to member.
      state.isMember = action.payload === "member" || action.payload === "customer";
    },
    /**
     * Adopt the server's authorization answer (GET /auth/capabilities).
     *
     * The client used to decide both of these itself: isAdmin from the role
     * STRING, and the permission list by looking up that same string in the
     * all-roles map. Both read users.role, which is drifted — it is not what
     * the API authorizes on (role_id is). On gymnastics-of-york five staff
     * accounts were handed a permission set that disagreed with the server's
     * in both directions: one was offered 22 actions the API refuses, and
     * another was shown 6 of the 70 she is actually allowed.
     *
     * isStaff / isMember stay on their own derivation: the role text is
     * reliable for the member-vs-staff distinction (every member account
     * carries member/customer), and only the admin bit was wrong.
     */
    setAuthzFromCapabilities: (state, action) => {
      const caps = action.payload || {};
      state.isAdmin = !!caps.is_elevated;
      state.permissions = (caps.permissions || []).map((name: string) => ({ name }));
      state.permissionsLoaded = true;
    },
  },
});

export const {
  setUser,
  setIsAdmin,
  setIsStaff,
  setIsMember,
  setPermissions,
  setAuthzFromCapabilities,
} = authSlice.actions;

// <-- selector to “get” membershipDetails -->
export const selectIsAdmin = (state: RootState) => state.auth.isAdmin;
export const selectIsStaff = (state: RootState) => state.auth.isStaff;
export const selectIsMember = (state: RootState) => state.auth.isMember;
export const selectUser = (state: RootState) => state.auth.userDetails;
export const selectPermissions = (state: RootState) => state.auth.permissions;
export const selectPermissionsLoaded = (state: RootState) => state.auth.permissionsLoaded;

export default authSlice.reducer;
